import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../CSS/CRUDfiles.css';
import {
  CrudListShell,
  FilterBar,
  FormGrid,
  FormActions,
  ExpandableRow,
  DetailGrid,
  DetailPanel,
  SummaryRow,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  SearchInput,
  FilterSelect,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const SCHOOL_SEARCH = (school, ctx) =>
  `${school.name ?? ''} ${ctx.districtById.get(school.district_id) ?? 'Unknown District'}`;

const SCHOOL_FILTERS = [
  {
    key: 'district',
    label: 'District',
    allOption: { value: 'all', label: 'All districts' },
    options: (items, ctx) =>
      (ctx.districts || [])
        .map((d) => ({ value: String(d.district_id), label: d.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    predicate: (school, value) => String(school.district_id) === value,
  },
];

const SCHOOL_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (s) => s.name?.toLowerCase() ?? '' },
  {
    key: 'students',
    label: 'Student body',
    accessor: (s) => Number(s.student_body_count) || 0,
    type: 'number',
  },
  {
    key: 'district',
    label: 'District',
    accessor: (s, ctx) =>
      (ctx.districtById.get(s.district_id) ?? 'Unknown District').toLowerCase(),
  },
  {
    key: 'title_one',
    label: 'Title I',
    accessor: (s) => (s.title_one_status ? 1 : 0),
    type: 'number',
  },
];


const SchoolComponent = () => {
  const [allSchools, setAllSchools] = useState([]);
  const [currentSchool, setCurrentSchool] = useState({ /* initial school state */ });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const recordsPerPage = 10;
  const { authState } = useAuth();
  const [districts, setDistricts] = useState([]);
  const [types, setTypes] = useState([]);
  const [genres, setGenres] = useState([]);

  const navigate = useNavigate();

  const getDistrictName = (schoolId) => {
    const school = allSchools.find(s => s.school_id === schoolId);
    if (school) {
      const district = districts.find(d => d.district_id === school.district_id);
      return district ? district.name : 'Unknown District';
    }
    return 'Unknown District';
  };

  // Stable, Map-backed context so search/sort accessors avoid per-item scans
  // and the hook's memo only recomputes when districts actually change.
  const listContext = useMemo(
    () => ({
      districts,
      districtById: new Map(districts.map((d) => [d.district_id, d.name])),
    }),
    [districts]
  );

  const {
    items: processedSchools,
    resultCount,
    totalCount,
    isFiltered,
    filterSignature,
    search,
    filterList,
    sort,
    clear,
  } = useListControls(allSchools, {
    searchAccessor: SCHOOL_SEARCH,
    filters: SCHOOL_FILTERS,
    sortOptions: SCHOOL_SORT_OPTIONS,
    context: listContext,
  });

  // Feature-local pagination sits on top of the processed (searched/filtered/
  // sorted) array. Reset to page 1 on any search/filter change (keyed on the
  // actual state, not resultCount — equal-sized result sets must still reset),
  // and clamp as a safety net so a stale page can never render empty.
  const totalPages = Math.ceil(processedSchools.length / recordsPerPage) || 1;
  const displayedSchools = processedSchools.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSignature]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all schools
  useEffect(() => {
    fetchSchools();
    fetchDistricts();
    fetchGenres();
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    const url = `${backendURL}/api/schooltypes`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      setTypes(data);
    }
  };
  const fetchGenres = async () => {
    const url = `${backendURL}/api/schoolgenres`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      setGenres(data);
    }
  };
  const fetchDistricts = async () => {
    const url = `${backendURL}/api/districts`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      setDistricts(data);
    }
  }

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${backendURL}/api/schools`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      setAllSchools(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching schools:', error);
      setAllSchools([]);
    }
  };

  const handleFormChange = (e) => {
    setCurrentSchool({ ...currentSchool, [e.target.name]: e.target.value });
  };

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentSchool({ /* reset to initial state with empty fields */ });
    }
    setShowForm(!showForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentSchool.school_id) {
      await updateSchool(currentSchool.school_id, currentSchool);
    } else {
      await createSchool(currentSchool);
    }
    setCurrentSchool({ /* reset to initial state */ });
    setShowForm(false);
    fetchSchools();
  };

  const createSchool = async (schoolData) => {
    try {
      const response = await fetch(`${backendURL}/api/schools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(schoolData),
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to create school.');
      }
      fetchSchools();
    } catch (error) {
      console.error('Error creating school:', error);
      // Optionally, handle the error in the UI
    }
  };

  const updateSchool = async (schoolId, schoolData) => {
    try {
      const response = await fetch(`${backendURL}/api/schools/${schoolId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(schoolData),
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to update school.');
      }
      fetchSchools();
    } catch (error) {
      console.error('Error updating school:', error);
      // Optionally, handle the error in the UI
    }
  };

  const deleteSchool = async () => {
    try {
      const response = await fetch(`${backendURL}/api/schools/${schoolToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to delete school.');
      }
      fetchSchools();
    } catch (error) {
      console.error('Error deleting school:', error);
      // Optionally, handle the error in the UI
    }
    setSchoolToDelete(null);
    setShowModal(false);
  };

  const editSchool = (school) => {
    setCurrentSchool(school);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentSchool({/*setting back to initial state */})
    setShowForm(false);
  }

  // Pagination Controls
  const MAX_PAGES_DISPLAYED = 5; // Max number of pages to display in the pagination

  const getPaginationItems = () => {
    let items = [];

    // Calculate the range of pages to display
    let startPage = Math.max(currentPage - Math.floor(MAX_PAGES_DISPLAYED / 2), 1);
    let endPage = Math.min(startPage + MAX_PAGES_DISPLAYED - 1, totalPages);

    // Adjust the start page if we're near the end
    if (endPage - startPage + 1 < MAX_PAGES_DISPLAYED) {
      startPage = Math.max(endPage - MAX_PAGES_DISPLAYED + 1, 1);
    }

    // 'Previous' button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      />
    );

    // Page numbers
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // 'Next' button
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      />
    );

    return items;
  };

  const schoolForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
      {/* School Name */}
      <Form.Group controlId="name">
        <Form.Label>School Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter school name"
          name="name"
          onChange={handleFormChange}
          value={currentSchool.name}
        />
      </Form.Group>

      {/* Phone */}
      <Form.Group controlId="phone">
        <Form.Label>Phone</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter phone number"
          name="phone"
          onChange={handleFormChange}
          value={currentSchool.phone}
        />
      </Form.Group>

      {/* Street */}
      <Form.Group controlId="street">
        <Form.Label>Street</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter street address"
          name="street"
          onChange={handleFormChange}
          value={currentSchool.street}
        />
      </Form.Group>

      {/* City */}
      <Form.Group controlId="city">
        <Form.Label>City</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter city"
          name="city"
          onChange={handleFormChange}
          value={currentSchool.city}
        />
      </Form.Group>

      {/* State */}
      <Form.Group controlId="state">
        <Form.Label>State</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter state"
          name="state"
          onChange={handleFormChange}
          value={currentSchool.state}
        />
      </Form.Group>

      {/* Zip Code */}
      <Form.Group controlId="zip">
        <Form.Label>Zip</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter zip code"
          name="zip"
          onChange={handleFormChange}
          value={currentSchool.zip}
        />
      </Form.Group>
      {/* Student Body Count */}
      <Form.Group controlId="student_body_count">
        <Form.Label>Student Body Count</Form.Label>
        <Form.Control
          type="number"
          placeholder="Enter student body count"
          name="student_body_count"
          onChange={handleFormChange}
          value={currentSchool.student_body_count}
        />
      </Form.Group>
      {/* Title One Status */}
      <Form.Group controlId="title_one_status">
        <Form.Label>Title One Status</Form.Label>
        <Form.Check
          type="checkbox"
          label="Title One School"
          name="title_one_status"
          onChange={e => handleFormChange({
            target: {
              name: e.target.name,
              value: e.target.checked
            }
          })}
          checked={currentSchool.title_one_status}
        />
      </Form.Group>
      {/* District ID */}
      <Form.Group controlId="district_id">
        <Form.Label>District</Form.Label>
        <Form.Control
          as="select"
          name="district_id"
          onChange={handleFormChange}
          value={currentSchool.district_id}
        >
          <option value="">Select District</option>
          {districts.map(district => (
            <option key={district.district_id} value={district.district_id}>
              {district.name}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
      {/* Type ID - Assuming you have type data similar to districts */}
      <Form.Group controlId="type_id">
        <Form.Label>Type</Form.Label>
        <Form.Control
          as="select"
          name="type_id"
          onChange={handleFormChange}
          value={currentSchool.type_id}
        >
          <option value="">Select Type</option>
          {types.map(type => (
            <option key={type.type_id} value={type.type_id}>{type.type}</option>
          ))}
        </Form.Control>
      </Form.Group>
      {/* Genre ID - Assuming you have genre data similar to districts */}
      <Form.Group controlId="genre_id">
        <Form.Label>Genre</Form.Label>
        <Form.Control
          as="select"
          name="genre_id"
          onChange={handleFormChange}
          value={currentSchool.genre_id}
        >
          <option value="">Select Genre</option>
          {genres.map(genre => (
            <option key={genre.genre_id} value={genre.genre_id}>{genre.type}</option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Add other fields similarly */}
      <FormActions>
        <Button type="submit">Submit</Button>
        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
      </FormActions>
      </FormGrid>
    </Form>
  );

  const searchFilters = (
    <FilterBar
      summary={(
        <ResultSummary
          count={resultCount}
          total={totalCount}
          noun="school"
          onClear={clear}
          isFiltered={isFiltered}
        />
      )}
    >
      <SearchInput
        label="Search schools"
        placeholder="School or district name"
        {...search}
      />
      {filterList.map((f) => (
        <FilterSelect key={f.filterKey} {...f} />
      ))}
      <SortControl {...sort} />
    </FilterBar>
  );

  const buildSubtitle = (school) => {
    const cityState = [school.city, school.state].filter(Boolean).join(', ');
    return cityState || 'No address on file';
  };

  const buildPills = (school) => {
    const pills = [{ label: getDistrictName(school.school_id) }];
    if (school.type) pills.push({ label: school.type });
    if (school.genre) pills.push({ label: school.genre });
    if (school.student_body_count != null && school.student_body_count !== '') {
      pills.push({ label: `Students: ${school.student_body_count}` });
    }
    if (school.title_one_status) pills.push({ label: 'Title I', variant: 'warning' });
    return pills;
  };

  return (
    <div>
      <CrudListShell
        addLabel="Add New School"
        listLabel="Show Schools List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Schools"
        form={schoolForm}
        filters={searchFilters}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No schools match the current search.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {displayedSchools.map((school) => (
          <ExpandableRow
            key={school.school_id}
            isOpen={openId === school.school_id}
            onToggle={() => setOpenId(openId === school.school_id ? null : school.school_id)}
            main={{ title: school.name, subtitle: buildSubtitle(school) }}
            pills={buildPills(school)}
            actions={(
              <>
                <Button size="sm" onClick={() => editSchool(school)} aria-label="Edit school">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setSchoolToDelete(school.school_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete school"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
            detail={(
              <DetailGrid columns={2}>
                <DetailPanel title="Contact">
                  <SummaryRow label="Phone" value={school.phone || 'None'} />
                  <SummaryRow label="Street" value={school.street || 'None'} />
                  <SummaryRow label="City" value={school.city || 'None'} />
                  <SummaryRow label="State" value={school.state || 'None'} />
                  <SummaryRow label="Zip" value={school.zip || 'None'} />
                </DetailPanel>
                <DetailPanel title="Classification">
                  <SummaryRow label="District" value={getDistrictName(school.school_id)} />
                  <SummaryRow label="Type" value={school.type || 'None'} />
                  <SummaryRow label="Genre" value={school.genre || 'None'} />
                  <SummaryRow
                    label="Student Body"
                    value={school.student_body_count ?? 'Not recorded'}
                  />
                  <SummaryRow label="Title I" value={school.title_one_status ? 'Yes' : 'No'} />
                </DetailPanel>
              </DetailGrid>
            )}
          />
        ))}
      </CrudListShell>

      {/* Pagination */}
      {showForm ? "" : (<Pagination className="justify-content-center">
        {getPaginationItems()}
      </Pagination> )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteSchool}
        bodyText="Are you sure you want to delete this school?"
      />
    </div>
  );
};

export default SchoolComponent;
